import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import EPRItemCard, { type ScorerSide } from '../components/epr/EPRItemCard';
import EPRComponentSection from '../components/epr/EPRComponentSection';
import { useToast } from '../components/ui/Toast';
import { useAutoSave } from '../hooks/useAutoSave';
import type {
  EPRSubmission, EPRItem, Score1to5,
} from '../types/eprFramework';
import { DEFAULT_COMPONENT_SOURCES, KPI_SCALE, OKR_SCALE } from '../types/eprFramework';
import type { MagicLinkToken } from '../types/magicLinkToken';
import {
  getSubmission, patchItem, submitByEmployee, submitByManager,
  addEmployeeItem, removeEmployeeItem,
  EPRSubmissionError,
} from '../services/eprSubmissionService';
import {
  validateSubmissionForSubmit, computeItemFinalScore,
  type ValidationIssue,
} from '../services/eprScoreEngine';
import { markTokenUsed, MagicLinkError } from '../services/magicLinkService';
import {
  ensureAnonymousAuth, fetchKrsForEmployee, derivePeriodFromDates,
  type SyncedKr,
} from '../services/okrTrackerSyncService';
import OkrPickerCard from '../components/epr/OkrPickerCard';
import KpiEditorCard from '../components/epr/KpiEditorCard';
import RecipientBanner from '../components/magic/RecipientBanner';
import FormHeaderCard from '../components/magic/FormHeaderCard';
import type { EPRSubmissionHeader } from '../types/eprFramework';

/**
 * EPRMagicForm — magic-link page for Step 1 EPR submission.
 *
 * Renders both employee (self) and manager modes. Mode is set by the
 * dispatcher based on token.action; switches scorer column, read-only
 * fields, submit handler, and validator.
 *
 * Implements:
 *  - §C.7 evidence gating (delegated to EPRItemCard)
 *  - §L.1 single-column card-based layout
 *  - §L.2 auto-save (per-field debounce), inline validation, scroll to issue
 *  - Decision #5 — token expired mid-form: read-only + draft preserved
 */

export interface EPRMagicFormProps {
  token: MagicLinkToken;
  mode: ScorerSide;
}

const EPRMagicForm: React.FC<EPRMagicFormProps> = ({ token, mode }) => {
  const toast = useToast();
  const save = useAutoSave();

  const [submission, setSubmission] = useState<EPRSubmission | null>(null);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState<string | null>(null);

  const [issues, setIssues]                 = useState<ValidationIssue[]>([]);
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const [submittedNow, setSubmittedNow]     = useState(false);
  const [expiredMidForm, setExpiredMidForm] = useState(false);

  const mainRef = useRef<HTMLDivElement>(null);

  // ─── Load submission ───────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSubmission(token.targetDocId)
      .then(s => { if (alive) setSubmission(s); })
      .catch(e => { if (alive) setLoadError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token.targetDocId]);

  // ─── Derived state ─────────────────────────────────────────────────
  const isInteractive = useMemo(() => {
    if (expiredMidForm || !submission) return false;
    if (mode === 'employee') return submission.status === 'draft';
    return submission.status === 'employee_submitted' || submission.status === 'draft';
  }, [submission, mode, expiredMidForm]);

  const componentSources = useMemo(
    () => submission?.templateSnapshot?.componentSources ?? DEFAULT_COMPONENT_SOURCES,
    [submission],
  );
  const evidenceTrigger = submission?.templateSnapshot?.scaleConfig.evidenceTrigger;
  const okrScaleDef = submission?.templateSnapshot?.scoringScales.okr ?? OKR_SCALE;
  const kpiScaleDef = submission?.templateSnapshot?.scoringScales.kpi ?? KPI_SCALE;

  // The employee defines their own OKR/KPI items while drafting (the
  // low-friction flow — HR seeds nothing at launch).
  const employeeBuildsItems =
    mode === 'employee' && !!submission && submission.status === 'draft' && !expiredMidForm;

  // ─── OKR Tracker sync (on form open, employee drafting only) ───────
  const [krs, setKrs]               = useState<SyncedKr[]>([]);
  const [krsLoading, setKrsLoading] = useState(false);
  const [krsError, setKrsError]     = useState<string | null>(null);

  const okrPickerEnabled =
    employeeBuildsItems
    && submission.weights.okr > 0
    && componentSources.okr === 'okrTracker';

  const loadKrs = useCallback(async () => {
    if (!submission) return;
    setKrsLoading(true);
    setKrsError(null);
    try {
      await ensureAnonymousAuth();
      const fromIso = submission.header?.reviewPeriodFrom;
      const year = fromIso ? new Date(fromIso).getFullYear() : new Date().getFullYear();
      const result = await fetchKrsForEmployee({
        email:  token.recipientEmail,
        year:   Number.isFinite(year) ? year : new Date().getFullYear(),
        period: derivePeriodFromDates(fromIso, submission.header?.reviewPeriodTo),
        level:  submission.weights.leadership > 0 ? 'supervisor' : 'general',
      });
      setKrs(result.krs);
    } catch (e) {
      setKrsError((e as Error).message || 'Sync failed');
    } finally {
      setKrsLoading(false);
    }
  }, [submission, token.recipientEmail]);

  // One sync per submission — the ref guard keeps later submission-state
  // updates (every auto-save patch replaces the object) from re-fetching.
  const syncedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!okrPickerEnabled || !submission) return;
    if (syncedForRef.current === submission.id) return;
    syncedForRef.current = submission.id;
    void loadKrs();
  }, [okrPickerEnabled, submission, loadKrs]);

  const componentsToRender = useMemo(() => {
    if (!submission) return [];
    const list: Array<{ key: EPRItem['component']; items: EPRItem[]; weight: number }> = [
      { key: 'okr',        items: submission.okrItems,        weight: submission.weights.okr },
      { key: 'kpi',        items: submission.kpiItems,        weight: submission.weights.kpi },
      { key: 'core',       items: submission.coreItems,       weight: submission.weights.core },
      { key: 'leadership', items: submission.leadershipItems, weight: submission.weights.leadership },
    ];
    return list.filter(c => c.weight > 0);
  }, [submission]);

  // ─── Local optimistic patch ────────────────────────────────────────
  // Narrowed bucket key — only the four EPRItem[] fields on EPRSubmission.
  // Literal union (not a mapped conditional type) for clearer error msgs
  // and to avoid TS's "undefined index" false-positive that mapped types
  // can trip when other fields on the parent are optional.
  // ENGINEERING.md §4: no `any` in production code.
  type ItemBucketKey = 'okrItems' | 'kpiItems' | 'coreItems' | 'leadershipItems';

  const updateLocalItem = useCallback((itemId: string, patch: Partial<EPRItem>) => {
    setSubmission(curr => {
      if (!curr) return curr;
      const buckets: ItemBucketKey[] = ['okrItems', 'kpiItems', 'coreItems', 'leadershipItems'];
      const next: EPRSubmission = { ...curr };
      for (const b of buckets) {
        const arr: EPRItem[] = next[b];
        const idx = arr.findIndex(it => it.itemId === itemId);
        if (idx >= 0) {
          const updated: EPRItem = { ...arr[idx], ...patch };
          updated.itemFinalScore = computeItemFinalScore(updated);
          const newArr = [...arr];
          newArr[idx] = updated;
          next[b] = newArr;
          break;
        }
      }
      return next;
    });
  }, []);

  // ─── Auto-save dispatcher ──────────────────────────────────────────
  const handlePatch = useCallback(
    async (
      itemId: string,
      side: 'employeeSelfScore' | 'managerScore',
      patch: { score?: Score1to5; notesEvidence?: string; target?: string; actual?: string; achievementPct?: number },
    ) => {
      if (!submission) return;
      // Optimistic local update
      const localPatch: Partial<EPRItem> = {};
      if (typeof patch.score === 'number')          localPatch[side] = patch.score;
      if (typeof patch.notesEvidence === 'string')  localPatch.notesEvidence = patch.notesEvidence;
      if (typeof patch.target === 'string')         localPatch.target = patch.target;
      if (typeof patch.actual === 'string')         localPatch.actual = patch.actual;
      if (typeof patch.achievementPct !== 'undefined') localPatch.achievementPct = patch.achievementPct;
      updateLocalItem(itemId, localPatch);

      // Auto-save
      save.markSaving();
      try {
        await patchItem(submission.id, itemId, side, patch);
        save.markSaved();
      } catch (e) {
        const err = e as EPRSubmissionError | MagicLinkError | Error;
        const msg = err.message || 'Save failed';
        save.markError(msg);
        if (err instanceof EPRSubmissionError && err.code === 'wrong_state') {
          setExpiredMidForm(true);
          toast.show('Form is locked. Your draft is saved up to the last successful save.', { icon: 'warning' });
        } else {
          toast.show(`Save failed: ${msg}`, { icon: 'error' });
        }
      }
    },
    [submission, save, toast, updateLocalItem],
  );

  // ─── Employee-created items (OKR pick / manual KPI rows) ───────────
  const appendLocalItem = useCallback((bucket: 'okrItems' | 'kpiItems', item: EPRItem) => {
    setSubmission(curr => {
      if (!curr) return curr;
      if (curr[bucket].some(it => it.itemId === item.itemId)) return curr;
      return { ...curr, [bucket]: [...curr[bucket], item] };
    });
  }, []);

  const removeLocalItem = useCallback((bucket: 'okrItems' | 'kpiItems', itemId: string) => {
    setSubmission(curr => {
      if (!curr) return curr;
      return { ...curr, [bucket]: curr[bucket].filter(it => it.itemId !== itemId) };
    });
  }, []);

  const handlePickOkr = useCallback(async (kr: SyncedKr) => {
    if (!submission) return;
    const itemName = kr.krNumber ? `${kr.krNumber} — ${kr.title}` : kr.title;
    save.markSaving();
    try {
      await addEmployeeItem(submission.id, {
        component:   'okr',
        itemId:      kr.krId,
        itemName,
        description: kr.objectiveTitle,
        target:      kr.target,
      });
      appendLocalItem('okrItems', {
        itemId: kr.krId, component: 'okr', itemName,
        description: kr.objectiveTitle, target: kr.target, addedBy: 'employee',
      });
      save.markSaved();
    } catch (e) {
      save.markError((e as Error).message);
      toast.show(`Could not add the OKR: ${(e as Error).message}`, { icon: 'error' });
    }
  }, [submission, save, toast, appendLocalItem]);

  const handleAddKpi = useCallback(async (name: string, target?: string) => {
    if (!submission) return;
    const arr = new Uint8Array(4);
    crypto.getRandomValues(arr);
    const itemId = `kpi_${Array.from(arr, b => b.toString(16).padStart(2, '0')).join('')}`;
    save.markSaving();
    try {
      await addEmployeeItem(submission.id, { component: 'kpi', itemId, itemName: name, target });
      appendLocalItem('kpiItems', {
        itemId, component: 'kpi', itemName: name, target, addedBy: 'employee',
      });
      save.markSaved();
    } catch (e) {
      save.markError((e as Error).message);
      throw e;  // KpiEditorCard surfaces the message inline
    }
  }, [submission, save, appendLocalItem]);

  const handleRemoveEmployeeItem = useCallback(async (
    bucket: 'okrItems' | 'kpiItems',
    itemId: string,
  ) => {
    if (!submission) return;
    save.markSaving();
    try {
      await removeEmployeeItem(submission.id, itemId);
      removeLocalItem(bucket, itemId);
      save.markSaved();
    } catch (e) {
      save.markError((e as Error).message);
      toast.show(`Could not remove the item: ${(e as Error).message}`, { icon: 'error' });
    }
  }, [submission, save, toast, removeLocalItem]);

  // ─── Submit ────────────────────────────────────────────────────────
  const handleSubmitClick = useCallback(() => {
    if (!submission) return;
    const v = validateSubmissionForSubmit(submission, mode);
    setIssues(v);
    if (v.length > 0) {
      // Scroll to first issue
      const firstId = v.find(i => i.itemId)?.itemId;
      if (firstId) {
        const el = document.getElementById(`epr-item-${firstId}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      toast.show(`${v.length} item${v.length === 1 ? '' : 's'} need fixing before submit`, { icon: 'error' });
      return;
    }
    setConfirmOpen(true);
  }, [submission, mode, toast]);

  const handleConfirmedSubmit = useCallback(async () => {
    if (!submission) return;
    setSubmitInFlight(true);
    try {
      if (mode === 'employee') {
        await submitByEmployee(submission.id, token.recipientId);
      } else {
        await submitByManager(submission.id, token.recipientId);
      }
      await markTokenUsed(token.id);
      setSubmittedNow(true);
    } catch (e) {
      const err = e as EPRSubmissionError | MagicLinkError | Error;
      if (err instanceof EPRSubmissionError && err.code === 'validation_failed' && err.issues) {
        setIssues(err.issues);
        setConfirmOpen(false);
        toast.show('Validation failed: server rejected the submission', { icon: 'error' });
      } else if (err instanceof MagicLinkError && err.code === 'expired') {
        setExpiredMidForm(true);
        setConfirmOpen(false);
        toast.show('Your link has expired.', { icon: 'warning' });
      } else {
        toast.show(err.message || 'Submit failed', { icon: 'error' });
      }
    } finally {
      setSubmitInFlight(false);
    }
  }, [submission, mode, token, toast]);

  // ─── Terminal states ───────────────────────────────────────────────
  if (loading) {
    return (
      <CenterMessage tone="info" title="Loading..." body="Fetching your EPR data..." />
    );
  }
  if (loadError || !submission) {
    return (
      <CenterMessage
        tone="error"
        title="EPR not found"
        body={loadError ?? 'No submission exists for this link. Contact HR.'}
      />
    );
  }

  if (submittedNow) {
    return (
      <CenterMessage
        tone="success"
        title="Thank you"
        body={
          mode === 'employee'
            ? 'Your EPR self-assessment is submitted. Your manager will continue the review.'
            : 'Your EPR manager review is submitted. HR will continue to the consolidation step.'
        }
      />
    );
  }

  // Already submitted before reaching this page (idempotent re-open)
  if (
    (mode === 'employee' && submission.status !== 'draft') ||
    (mode === 'manager' && (submission.status === 'manager_submitted' || submission.status === 'locked'))
  ) {
    return (
      <CenterMessage
        tone="info"
        title="Already submitted"
        body={
          mode === 'employee'
            ? 'Your self-assessment is already submitted for this cycle.'
            : 'Manager review is already submitted for this employee.'
        }
      />
    );
  }

  // ─── Main form ─────────────────────────────────────────────────────
  const totalIssues = issues.length;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top banner */}
      <header className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-stone-500">
              Step 1 — EPR {mode === 'employee' ? 'Self Assessment' : 'Manager Review'}
            </div>
            <div className="text-base font-semibold text-stone-900 truncate">
              {token.recipientName ?? token.recipientEmail}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DisclosureBadge
              icon="schedule"
              label={`Valid until ${formatDate(token.expiresAt.toDate())}`}
              tone="neutral"
            />
            <DisclosureBadge
              icon={save.state.status === 'saving' ? 'sync' :
                    save.state.status === 'error' ? 'error' :
                    save.state.status === 'saved' ? 'check_circle' : 'cloud_done'}
              label={save.label ?? 'Auto-save on'}
              tone={save.state.status === 'error' ? 'warning' :
                    save.state.status === 'saved' ? 'success' : 'info'}
            />
          </div>
        </div>
        {totalIssues > 0 && (
          <div className="bg-red-50 border-t border-red-200">
            <div className="max-w-3xl mx-auto px-4 py-2 text-sm text-red-800">
              <span className="font-semibold">{totalIssues}</span> item{totalIssues === 1 ? '' : 's'} need fixing before submit.
            </div>
          </div>
        )}
        {expiredMidForm && (
          <div className="bg-amber-50 border-t border-amber-200">
            <div className="max-w-3xl mx-auto px-4 py-2 text-sm text-amber-900">
              Link expired. Form is now read-only. Your draft is saved up to the last successful save.
              Contact HR to re-issue the link.
            </div>
          </div>
        )}
      </header>

      {/* Body */}
      <main ref={mainRef} className="max-w-3xl mx-auto px-4 py-6 space-y-8 pb-32">
        {/* Identity confirmation — catch accidental forward. The masked
            email makes it obvious to a bystander that this isn't their
            form so they bail out before filling. See RecipientBanner. */}
        <RecipientBanner
          recipientEmail={token.recipientEmail}
          recipientName={token.recipientName}
        />
        {/* Canonical form header (EPR Form.docx) — every field auto-filled
            from the launch snapshot; nothing here is typed by anyone. */}
        <FormHeaderCard
          title="Employee Performance Review"
          rows={[
            { label: 'Employee Name', value: submission.header?.employeeName ?? (mode === 'employee' ? token.recipientName : undefined) },
            { label: 'Position',      value: submission.header?.position },
            { label: 'Department',    value: submission.header?.department },
            {
              label: 'Review Period',
              value: formatPeriod(submission.header),
            },
            { label: 'Supervisor',    value: submission.header?.supervisorName },
            { label: 'Review Date',   value: formatDate(new Date()) },
          ]}
        />
        {componentsToRender.map(group => {
          const filled = group.items.filter(it => {
            const s = mode === 'employee' ? it.employeeSelfScore : it.managerScore;
            return s != null;
          }).length;
          return (
            <EPRComponentSection
              key={group.key}
              component={group.key}
              weight={group.weight}
              filledCount={filled}
              totalCount={group.items.length}
            >
              {group.key === 'okr' && okrPickerEnabled && (
                <OkrPickerCard
                  krs={krs}
                  pickedIds={new Set(submission.okrItems.map(it => it.itemId))}
                  loading={krsLoading}
                  error={krsError}
                  disabled={!isInteractive}
                  onPick={kr => void handlePickOkr(kr)}
                  onUnpick={krId => void handleRemoveEmployeeItem('okrItems', krId)}
                  onRetry={() => void loadKrs()}
                />
              )}
              {group.items.map(item => (
                <div key={item.itemId} id={`epr-item-${item.itemId}`}>
                  <EPRItemCard
                    item={item}
                    scorer={mode}
                    disabled={!isInteractive}
                    issues={issues}
                    competencyDefs={
                      group.key === 'core' ? submission.templateSnapshot?.coreCompetencies :
                      group.key === 'leadership' ? submission.templateSnapshot?.leadershipSkills :
                      undefined
                    }
                    scaleDef={
                      group.key === 'okr' ? okrScaleDef :
                      group.key === 'kpi' ? kpiScaleDef :
                      undefined
                    }
                    evidenceTrigger={evidenceTrigger}
                    onScoreChange={score =>
                      handlePatch(item.itemId,
                        mode === 'employee' ? 'employeeSelfScore' : 'managerScore',
                        { score })}
                    onEvidenceChange={value =>
                      handlePatch(item.itemId,
                        mode === 'employee' ? 'employeeSelfScore' : 'managerScore',
                        { notesEvidence: value })}
                    onTargetChange={mode === 'employee'
                      ? value => handlePatch(item.itemId, 'employeeSelfScore', { target: value })
                      : undefined}
                    onActualChange={mode === 'employee'
                      ? value => handlePatch(item.itemId, 'employeeSelfScore', { actual: value })
                      : undefined}
                    onAchievementChange={mode === 'employee'
                      ? value => handlePatch(item.itemId, 'employeeSelfScore', { achievementPct: value ?? 0 })
                      : undefined}
                    onRemove={
                      employeeBuildsItems
                      && (group.key === 'okr' || group.key === 'kpi')
                      && item.addedBy === 'employee'
                        ? () => void handleRemoveEmployeeItem(
                            group.key === 'okr' ? 'okrItems' : 'kpiItems',
                            item.itemId,
                          )
                        : undefined
                    }
                  />
                </div>
              ))}
              {group.key === 'kpi'
                && employeeBuildsItems
                && componentSources.kpi === 'employeeInput' && (
                <KpiEditorCard
                  disabled={!isInteractive}
                  onAddKpi={handleAddKpi}
                />
              )}
            </EPRComponentSection>
          );
        })}
      </main>

      {/* Sticky footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-stone-600 truncate">
            {save.label ?? 'Changes saved automatically'}
          </div>
          <Button
            variant="submit"
            icon="send"
            disabled={!isInteractive}
            loading={submitInFlight}
            onClick={handleSubmitClick}
          >
            Submit
          </Button>
        </div>
      </footer>

      {/* Confirm dialog */}
      {confirmOpen && (
        <ConfirmDialog
          title="Confirm submit?"
          body={
            mode === 'employee'
              ? 'Once submitted, this self-assessment cannot be edited. Your manager will continue.'
              : 'Once submitted, the manager review cannot be edited except via a calibration adjustment.'
          }
          confirmLabel="Submit"
          loading={submitInFlight}
          onConfirm={handleConfirmedSubmit}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Helpers / sub-components ──────────────────────────────────────

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * "H1 2026 (1 Jan – 30 Jun 2026)" when ISO dates are present, else just
 * the label, else undefined so the row renders "—".
 */
function formatPeriod(header?: EPRSubmissionHeader): string | undefined {
  if (!header) return undefined;
  const { reviewPeriodLabel, reviewPeriodFrom, reviewPeriodTo } = header;
  if (reviewPeriodFrom && reviewPeriodTo) {
    const from = formatDate(new Date(reviewPeriodFrom));
    const to   = formatDate(new Date(reviewPeriodTo));
    return reviewPeriodLabel ? `${reviewPeriodLabel} (${from} – ${to})` : `${from} – ${to}`;
  }
  return reviewPeriodLabel;
}

interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, body, confirmLabel, loading, onConfirm, onCancel,
}) => (
  <div
    className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
  >
    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
      <h3 className="text-lg font-semibold text-stone-900 mb-2">{title}</h3>
      <p className="text-sm text-stone-700 leading-relaxed mb-5">{body}</p>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="submit" onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  </div>
);

export default EPRMagicForm;
